package com.siriusxm.pia.views.unifiedaggregator.events

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.siriusxm.pia.components.TimeRange
import com.siriusxm.pia.components.simpleSelectNullable
import com.siriusxm.pia.components.spinner
import com.siriusxm.pia.components.timeRangeSelect
import unifiedcontent.aggregator.CloudEvent
import unifiedcontent.aggregator.CloudEventList
import unifiedcontent.aggregator.EventQueryOption
import unifiedcontent.aggregator.EventSearchStatus
import unifiedcontent.aggregator.EventSearchStatusState
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Instant

/**
 * Styles for the events view
 */
object EntityEventsStyles : StyleSheet() {
    val eventsHeader by style {
        display(DisplayStyle.Flex)
        width(100.percent)
        padding(5.px, 0.px)
        gap(5.px)
    }

    val loadingBlock by style {
        display(DisplayStyle.Flex)
        flex(1)
        gap(5.px)
        padding(5.px)
        alignItems(AlignItems.Center)
        fontWeight(700)
    }
}

/** The display labels for each EventQueryOption value. */
private val eventQueryOptionLabels = listOf(
    "Outgoing Events" to EventQueryOption.Output,
    "Incoming Events" to EventQueryOption.Incoming
)

/**
 * A header bar displayed above event lists that provides controls for selecting the query
 * direction (outgoing/incoming), an optional event type filter, and a time range picker.
 * Also shows a loading indicator when events are being fetched.
 *
 * @param loading whether an event query is currently in progress
 * @param loadingText text shown next to the spinner while loading
 * @param selectedTimeRange the currently selected time range, or null if none
 * @param selectedQueryOption the currently selected event query direction, or null if the user
 *   must choose one before results are shown
 * @param showQueryOptions whether to display the outgoing/incoming query option selector
 * @param selectedEventType the currently selected event type filter, or null for all types.
 *   Only displayed when [showEventTypeFilter] is true.
 * @param showEventTypeFilter whether to display the event type filter control
 * @param earliest the earliest instant the time range picker allows; defaults to the events
 *   retention cutoff, since every events search runs against the same retained logs (UCA-1168)
 * @param onUpdate called when any selection changes with the updated time range, query option,
 *   and event type filter values
 */
@Composable
fun eventsSelectionHeader(
    loading: Boolean,
    loadingText: String = "Loading events...",
    selectedTimeRange: TimeRange? = null,
    selectedQueryOption: EventQueryOption? = null,
    showQueryOptions: Boolean = false,
    selectedEventType: EventTypeFilterOption? = null,
    showEventTypeFilter: Boolean = false,
    earliest: Instant? = remember { eventRetentionCutoff() },
    onUpdate: (range: TimeRange?, queryOption: EventQueryOption?, eventType: EventTypeFilterOption?) -> Unit
) {
    Div({
        classes(EntityEventsStyles.eventsHeader)
    }) {
        Div({
            classes(EntityEventsStyles.loadingBlock)
        }) {
            if (loading) {
                spinner()
                if (loadingText.isNotBlank()) {
                    Text(loadingText)
                }
            }
        }

        if (showQueryOptions) {
            Div {
                simpleSelectNullable(selectedQueryOption, eventQueryOptionLabels, "Select events") {
                    onUpdate(selectedTimeRange, it, selectedEventType)
                }
            }
        }

        if (showEventTypeFilter) {
            Div {
                EventTypeFilterControl(selectedEventType) { updated ->
                    onUpdate(selectedTimeRange, selectedQueryOption, updated)
                }
            }
        }

        Div {
            timeRangeSelect(
                defaultTimeRanges,
                selected = selectedTimeRange,
                earliest = earliest,
                note = EVENT_RETENTION_NOTE.takeIf { earliest != null }
            ) { range ->
                onUpdate(range, selectedQueryOption, selectedEventType)
            }
        }
    }
}

/**
 * Executes a poll to load events. Since each events API has a different endpoint, the caller needs to
 * manage the actual call. The query id is passed to the caller.
 *
 * [onUpdate] is called with each poll's raw batch, which is **not** guaranteed to be a superset of
 * an earlier one for the same queryId — a later intermediate poll can come back with fewer events
 * than one before it. Most callers want the accumulated, deduplicated result instead; see
 * [loadAccumulatedEvents].
 */
suspend fun loadEvents(
    call: suspend (queryId: String?) -> EventSearchStatus,
    onUpdate: suspend (CloudEventList) -> Unit = {}
): EventSearchStatus {
    var status = call(null)
    while (status.state == EventSearchStatusState.IN_PROGRESS) {
        onUpdate(status.results.orEmpty())
        delay(500)
        status = call(status.id)
    }
    onUpdate(status.results.orEmpty())
    return status
}

/**
 * Like [loadEvents], but [onUpdate] receives every event seen so far this search — accumulated
 * across polls and deduplicated — instead of a single poll's raw batch.
 *
 * This exists because [loadEvents]'s own contract (a later poll is not guaranteed to be a superset
 * of an earlier one) means a caller that simply replaces its displayed events with each raw batch
 * can watch already-found events disappear mid-search. Centralised here rather than left to each
 * caller to reimplement, since every current caller wants the same accumulate-and-dedupe behaviour.
 *
 * Events are deduplicated by the `(source, id)` pair — per [CloudEvent]'s own contract, only those
 * two values together are guaranteed unique. Keeping the pair as the map key avoids delimiter
 * collisions between otherwise distinct strings. The accumulated list is resorted newest-first on
 * every update, matching what callers displayed before this helper existed.
 */
suspend fun loadAccumulatedEvents(
    call: suspend (queryId: String?) -> EventSearchStatus,
    onUpdate: suspend (CloudEventList) -> Unit = {}
): EventSearchStatus {
    val seenEvents = LinkedHashMap<Pair<String, String>, CloudEvent>()
    return loadEvents(call) { batch ->
        batch.forEach { seenEvents[eventIdentity(it.source, it.id)] = it }
        onUpdate(seenEvents.values.sortedByDescending { it.time })
    }
}

/** The collision-safe identity CloudEvents defines for duplicate detection. */
internal fun eventIdentity(source: String, id: String): Pair<String, String> = source to id
